import type { Listing } from '@thevillages/schemas';

import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import type { DataLayerData } from '../../types/data-layer-data';
import type { DataLayerEvent } from '../../types/data-layer-event';
import type { ListingJsonLd } from '../../types/listing-json-ld';

import { Environment } from '../../../environments/environment';
import { ListingToJsonLd } from '../../utils/listing-to-json-ld';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private readonly _document = inject(DOCUMENT);
  private readonly _metaService = inject(Meta);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _titleService = inject(Title);

  private _listing?: Listing;

  constructor() {
    // Ensure the data layer is ready before posting a message to the parent window.
    if (isPlatformBrowser(this._platformId)) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- window.dataLayer's declared type is optimistic; GTM may not have initialized it yet at runtime.
      window.dataLayer = window.dataLayer || [];
    }
  }

  private _formatJsonLd(url: string): ListingJsonLd | undefined {
    if (!this._listing) {
      return undefined;
    }

    // Format the listing in the correct JSON-LD format.
    return ListingToJsonLd(this._listing, url);
  }

  private _removeArticleData(): void {
    this._metaService.removeTag("property='article:author'");
    this._metaService.removeTag("property='article:modified_time'");
    this._metaService.removeTag("property='article:published_time'");
    this._metaService.removeTag("property='article:publisher'");
    this._metaService.removeTag("property='article:tag'");
  }

  private _removeOgData(): void {
    this._metaService.removeTag("property='og:description'");
    this._metaService.removeTag("property='og:image:alt'");
    this._metaService.removeTag("property='og:image:height'");
    this._metaService.removeTag("property='og:image:width'");
    this._metaService.removeTag("property='og:image'");
    this._metaService.removeTag("property='og:site_name'");
    this._metaService.removeTag("property='og:title'");
    this._metaService.removeTag("property='og:type'");
    this._metaService.removeTag("property='og:url'");
  }

  private _removeTag(tag: string): void {
    const _tag = this._document.head.querySelector(tag);

    if (!_tag) {
      return;
    }

    this._document.head.removeChild(_tag);
  }

  private _removeTwitterData(): void {
    this._metaService.removeTag("name='twitter:card'");
    this._metaService.removeTag("name='twitter:description'");
    this._metaService.removeTag("name='twitter:image:alt'");
    this._metaService.removeTag("name='twitter:image'");
    this._metaService.removeTag("name='twitter:title'");
  }

  private _setArticleData(): void {
    const _listing = this._listing;

    if (!_listing) {
      return;
    }

    this._metaService.updateTag({
      content: Environment.community === 'villages' ? 'The Villages, FL' : 'Middleton, FL',
      property: 'article:author',
    });
    this._metaService.updateTag({
      content: new Date(_listing.lastUpdated ?? _listing.date).toDateString(),
      property: 'article:modified_time',
    });
    this._metaService.updateTag({
      content: new Date(_listing.date).toISOString(),
      property: 'article:published_time',
    });
    this._metaService.updateTag({
      content: 'https://www.facebook.com/thevillages',
      property: 'article:publisher',
    });
    this._metaService.updateTag({
      content: `${_listing.baths?.value ?? ''} bath`,
      property: 'article:tag',
    });
    this._metaService.updateTag({ content: `${_listing.beds} bed`, property: 'article:tag' });
    this._metaService.updateTag({
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- This condition is necessary until we update the logic to fit the new types package.
      content: `${_listing.squareFeet?.total || ''} sqft`,
      property: 'article:tag',
    });
    this._metaService.updateTag({
      content: _listing.address.locality,
      property: 'article:tag',
    });
    this._metaService.updateTag({
      content: _listing.address.postalCode,
      property: 'article:tag',
    });
    this._metaService.updateTag({ content: _listing.address.region, property: 'article:tag' });
    this._metaService.updateTag({
      content: _listing.address.streetAddress,
      property: 'article:tag',
    });
    this._metaService.updateTag({
      content: _listing.model?.title ?? '',
      property: 'article:tag',
    });
  }

  private _setCanonicalUrl(url: string): void {
    // Get the link, if it exists.
    let link: HTMLLinkElement | null = this._document.querySelector("link[rel='canonical']");

    if (!link) {
      // Create a new link element if it doesn't exist.
      link = this._document.createElement('link');
      link.setAttribute('rel', 'canonical');

      this._document.head.appendChild(link);
    }

    // Update the href attribute
    link.setAttribute('href', url);
  }

  private _setDescription(description: string): void {
    this._metaService.updateTag({ content: description, name: 'description' });
  }

  private _setJsonLd(url: string): void {
    const _jsonLd = this._formatJsonLd(url);

    if (!_jsonLd) {
      return;
    }

    // Remove the current JSON-LD (if it exists).
    this._removeTag('script[type="application/ld+json"]');

    const _script = this._document.createElement('script');
    _script.type = 'application/ld+json';
    _script.textContent = JSON.stringify(_jsonLd, null, 2);

    // Add the JSON-LD script tag.
    this._document.head.appendChild(_script);
  }

  private _setOgData(url: string, description: string): void {
    const _listing = this._listing;

    if (!_listing) {
      return;
    }

    this._metaService.updateTag({
      content: _listing.address.streetAddress,
      property: 'og:title',
    });
    this._metaService.updateTag({ content: description, property: 'og:description' });
    this._metaService.updateTag({ content: url, property: 'og:url' });
    this._metaService.updateTag({ content: 'website', property: 'og:type' });
    this._metaService.updateTag({
      content: Environment.community === 'villages' ? 'The Villages' : 'Middleton by The Villages',
      property: 'og:site_name',
    });

    if (_listing.primaryImage) {
      this._metaService.updateTag({
        content: _listing.images?.[0] ?? _listing.primaryImage,
        property: 'og:image',
      });
      this._metaService.updateTag({
        content: `Image of ${_listing.address.streetAddress}`,
        property: 'og:image:alt',
      });
      this._metaService.updateTag({ content: '344', property: 'og:image:width' });
      this._metaService.updateTag({ content: '224', property: 'og:image:height' });
    }
  }

  private _setTitle(title: string): void {
    // Remove the current title (if it exists).
    this._removeTag('title');

    this._titleService.setTitle(title);
  }

  private _setTwitterData(description: string): void {
    const _listing = this._listing;

    if (!_listing) {
      return;
    }

    this._metaService.updateTag({ content: 'summary_large_image', name: 'twitter:card' });
    this._metaService.updateTag({
      content: _listing.address.streetAddress,
      name: 'twitter:title',
    });
    this._metaService.updateTag({ content: description, name: 'twitter:description' });

    if (_listing.images?.length) {
      this._metaService.updateTag({ content: _listing.images[0], name: 'twitter:image' });
      this._metaService.updateTag({
        content: `Image of ${_listing.address.streetAddress}`,
        name: 'twitter:image:alt',
      });
    }
  }

  private _updateDefault(): void {
    this._listing = undefined;

    this._setCanonicalUrl(`${Environment.hostUrl}/`);

    this._setTitle('The Villages® Homefinder - Find Your Dream Home');

    this._setDescription(
      "The Villages® Homefinder is the only source for new & pre-owned homes for sale in Florida's premier 55+ active adult community.",
    );

    // Remove the current JSON-LD (if it exists).
    this._removeTag('script[type="application/ld+json"]');

    this._removeOgData();

    this._removeTwitterData();

    this._removeArticleData();
  }

  private _updateListing(listing: Listing): void {
    this._listing = listing;

    let title = listing.address.streetAddress;
    if (listing.model?.title) {
      title += ` | ${listing.model.title}`;
    }
    if (listing.communities.some((c: { id: string; title: string }) => c.id === 'middleton')) {
      title += ' | Middleton, FL';
    } else {
      title += ` | ${Environment.community === 'villages' ? 'The Villages, FL' : 'Middleton, FL'}`;
    }

    // let description = `This home is listed for sale by Properties of The Villages. ${(listing.description ?? `${listing.beds} bed / ${listing.baths?.value ?? ''} bath / ${listing.squareFeet?.total ?? ''} sqft`)}`;
    let description;
    if (listing.saleType === 'homesite') {
      description = `${listing.address.streetAddress} is listed for sale by Properties of The Villages. This is a homesite in ${listing.village.displayTitle}.`;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- This condition is necessary until we update the logic to fit the new types package.
      description = `${listing.address.streetAddress} is listed for sale by Properties of The Villages. This is a ${listing.beds}/${listing.baths?.value ?? ''} ${listing.saleType} ${listing.series?.title} home in ${listing.village.displayTitle}.`;
    }
    if (description.length > 160) {
      description = description.substring(0, 157).trimEnd() + '…';
    }
    const _url = `${Environment.hostUrl}/${listing.id}`;

    this._setCanonicalUrl(_url);

    this._setTitle(title);

    this._setDescription(description);

    this._setJsonLd(_url);

    this._setOgData(_url, description);

    this._setTwitterData(description);

    this._setArticleData();
  }

  public dataLayerPush(event: DataLayerEvent, data?: DataLayerData): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    window.dataLayer.push({
      event,
      ...data,
    });
  }

  public update(listing?: Listing): void {
    if (listing) {
      this._updateListing(listing);
    } else {
      this._updateDefault();
    }
  }
}
